import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	input,
} from '@angular/core';

import { AngularSvgIconModule } from 'angular-svg-icon';

import { CarouselModalComponent } from '@valk-nx/components/ui-carousel-modal/src/lib/carousel-modal';
import { ImageComponent } from '@valk-nx/components/ui-image/src/lib/image.component';
import { Sizes } from '@valk-nx/components/ui-image/src/lib/image.interface';
import {
	PhotoInterface,
	PhotoWithDimensionsInterface,
} from '@valk-nx/core/lib/interfaces/image.interface';

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: `vp-image-grid`,
	templateUrl: './image-grid.component.html',
	imports: [
		AngularSvgIconModule,
		CarouselModalComponent,
		CommonModule,
		ImageComponent,
	],
})
export class ImageGridComponent {
	images = input<PhotoInterface[]>([]);
	sticker = input<PhotoInterface | undefined>(undefined);
	size = input<'small' | 'base' | 'large'>('small');
	useCarousel = input<boolean>(false);
	className = input<string>('');

	sizes = Sizes;

	/**
	 * Returns the CSS class for the aspect ratio based on the size of the element.
	 * @returns {string} The CSS class for the aspect ratio.
	 */
	readonly aspectRatio = computed(() => {
		switch (this.size()) {
			case 'base':
				return 'aspect-square md:aspect-video';
			case 'large':
				return 'aspect-square md:aspect-video lg:aspect-anamorphic';
			case 'small':
			default:
				return 'aspect-square';
		}
	});

	readonly imagesWithDimensions = computed<PhotoWithDimensionsInterface[]>(
		() => {
			return this.images().map((photo, index) => {
				const { width, height } = this.getImageDimensions(
					this.images().length,
					index,
					this.size(),
				);
				return {
					...photo,
					width,
					height,
				};
			});
		},
	);

	getImageDimensions(
		numberOfImages: number,
		imageIndex: number,
		gridSize: 'small' | 'base' | 'large',
	): { width: number; height: number } {
		const dimensionMap = {
			1: {
				small: { width: 512, height: 512 },
				base: { width: 716, height: 356 },
				large: { width: 1024, height: 438 },
			},
			2: {
				small: { width: 512, height: 255 },
				base: { width: 356, height: 400 },
				large: { width: 509, height: 438 },
			},
			3: {
				small: [
					{ width: 512, height: 253 }, // first image
					{ width: 256, height: 256 }, // others
				],
				base: [
					{ width: 509, height: 576 }, // first image
					{ width: 509, height: 285 }, // others
				],
				large: [
					{ width: 800, height: 688 }, // first image
					{ width: 800, height: 339 }, // others
				],
			},
		};

		// Handle 1 or 2 images (single dimension object)
		if (numberOfImages <= 2) {
			const key = Math.min(numberOfImages, 2) as 1 | 2;
			return dimensionMap[key][gridSize] as {
				width: number;
				height: number;
			};
		}

		// Handle 3+ images (array of dimensions)
		const dimensions = dimensionMap[3][gridSize] as {
			width: number;
			height: number;
		}[];

		// First image gets index 0, all others get index 1
		const dimensionIndex = imageIndex === 0 ? 0 : 1;
		return dimensions[dimensionIndex];
	}
}
